import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../../../app/constants";
import { useAppDispatch } from "../../../../../app/hooks";
import { Union, UnionEezyPaymentInfo } from "../../unionsApi";
import { getPaymentInfosByUnionId } from "../../unionsSlice";
import { StyledTextField } from "../../unionsStyles";
import { isFulfilled } from "@reduxjs/toolkit";

interface Props {
  union: Union;
  setUnion: (union: Union) => void;
  selectedCompanyId: string;
  setSelectedCompanyId: (unionId: string) => void;
  title: string;
}

const UnionPaymentInfoModal = ({
  union,
  setUnion,
  selectedCompanyId,
  setSelectedCompanyId,
  title,
}: Props) => {
  const [refOptions, setRefOptions] = useState<UnionEezyPaymentInfo[]>([]);
  const [companyPaymentInfo, setCompanyPaymentInfo] =
    useState<UnionEezyPaymentInfo>(
      union.unionPaymentInfo.find(
        (item) => item.eezyCompanyId === selectedCompanyId,
      ) ?? { name: "", eezyCompanyId: "", paymentReferenceTemplate: "" },
    );

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    union?.id &&
      dispatch(
        getPaymentInfosByUnionId({
          unionId: union.id,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          const options = action.payload.sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          setRefOptions(options);
        }
      });
  }, [dispatch, union?.id]);

  const handleComplete = () => {
    if (selectedCompanyId === "new") {
      setUnion({
        ...union,
        unionPaymentInfo: [...union.unionPaymentInfo, companyPaymentInfo],
      });
    } else {
      setUnion({
        ...union,
        unionPaymentInfo: union.unionPaymentInfo.map((item) => {
          if (item.eezyCompanyId === selectedCompanyId) {
            return companyPaymentInfo;
          } else {
            return item;
          }
        }),
      });
    }
    setSelectedCompanyId("");
  };

  const cleanedRef = (companyPaymentInfo?.paymentReferenceTemplate ?? "")
    .replace(/\s+/g, "") // Remove whitespace
    .replace(/k/gi, "K") // Convert all 'k's to uppercase
    .replace(/v/gi, "V"); // Convert all 'v's to uppercase

  const disableContinue =
    companyPaymentInfo?.eezyCompanyId === "" ||
    cleanedRef === "" ||
    // Check that if there are K's or V's, they are not alone
    ((cleanedRef.match(/K/g) || []).length === 1 &&
      !cleanedRef.includes("KK")) ||
    ((cleanedRef.match(/V/g) || []).length === 1 && !cleanedRef.includes("VV"));

  return (
    <Box
      className={DEFAULT_DRAG_HANDLE_SELECTOR}
      sx={{
        width: "572px",
        display: "flex",
        flexDirection: "column",
        borderTopRightRadius: "12px",
        borderTopLeftRadius: "12px",
        cursor: "grab",
      }}
    >
      <Box
        sx={{
          fontSize: "16px",
          fontWeight: "600",
          padding: "15px 15px",
        }}
      >
        {title.toUpperCase()}
      </Box>

      {refOptions.length > 0 && (
        <>
          <Box className={NO_DRAG_SELECTOR} sx={{ cursor: "auto" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "15px 15px",
              }}
            >
              <Box
                sx={{
                  width: "40%",
                  textAlign: "end",
                  marginRight: "20px",
                  color: "rgba(0, 0, 0, 0.52)",
                }}
              >
                {t("settings:company")}
              </Box>
              <Box sx={{ width: "60%" }}>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={
                    selectedCompanyId === "new"
                      ? companyPaymentInfo.eezyCompanyId
                      : selectedCompanyId || ""
                  }
                  onChange={(e) =>
                    e.target.value &&
                    setCompanyPaymentInfo({
                      ...companyPaymentInfo,
                      eezyCompanyId: e.target.value,
                      name:
                        refOptions.find(
                          (item) => item.eezyCompanyId === e.target.value,
                        )?.name || "",
                    })
                  }
                  sx={{
                    backgroundColor: "rgba(249, 249, 250, 0.6)",
                  }}
                  disabled={
                    selectedCompanyId !== "" && selectedCompanyId !== "new"
                      ? true
                      : false
                  }
                  renderValue={(paymentId) => {
                    // Get name of company from refOptions or if not found, from unionPaymentInfo
                    // Otherwise show "Select..." as default value
                    const foundItem = refOptions.find(
                      (item) => item.eezyCompanyId === paymentId,
                    );
                    if (foundItem) {
                      return foundItem.name;
                    } else {
                      const unionPaymentInfoItem = union.unionPaymentInfo.find(
                        (item) => item.eezyCompanyId === paymentId,
                      );
                      return unionPaymentInfoItem
                        ? unionPaymentInfoItem.name
                        : t("common:select...");
                    }
                  }}
                >
                  {
                    /* Map through the optionsArray */
                    refOptions?.length > 0 &&
                      refOptions
                        .filter(
                          (option) =>
                            // Include the option if it is not in union.unionPaymentInfo or if it's the currently selected item
                            !union.unionPaymentInfo.some(
                              (unionOption) =>
                                unionOption.eezyCompanyId ===
                                option.eezyCompanyId,
                            ) || option.eezyCompanyId === selectedCompanyId,
                        )
                        .map((option) => (
                          <MenuItem
                            key={option.eezyCompanyId || ""}
                            value={option.eezyCompanyId || ""}
                          >
                            {option.name}
                          </MenuItem>
                        ))
                  }
                </Select>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "15px 15px",
              }}
            >
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  width: "40%",
                  textAlign: "end",
                  marginRight: "20px",
                  color: "rgba(0, 0, 0, 0.52)",
                }}
              >
                {t("settings:paymentReferenceTemplate")}
              </Box>
              <Box sx={{ width: "60%" }}>
                <StyledTextField
                  sx={{ width: "100%" }}
                  disabled={false}
                  value={companyPaymentInfo?.paymentReferenceTemplate || ""}
                  name="paymentReferenceTemplate"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    const cleanedValue = newValue
                      .replace(/\s+/g, "") // Remove whitespace
                      .replace(/k/gi, "K") // Convert all 'k's to uppercase
                      .replace(/v/gi, "V"); // Convert all 'v's to uppercase

                    const kAmount = (cleanedValue.match(/K/gi) || []).length;
                    const vAmount = (cleanedValue.match(/V/gi) || []).length;

                    (newValue === "" || /^[0-9kv]+$/i.test(cleanedValue)) && // Numbers + k + v only
                      kAmount <= 2 && // Max 2 k's
                      vAmount <= 2 && // Max 2 v's
                      ((kAmount > 1 && cleanedValue.includes("KK")) ||
                        kAmount <= 1) && // If 2 k's, they must be together
                      ((vAmount > 1 && cleanedValue.includes("VV")) ||
                        vAmount <= 1) && // If 2 v's, they must be together
                      cleanedValue.length <= 19 && // Max 19 characters
                      setCompanyPaymentInfo({
                        ...companyPaymentInfo,
                        paymentReferenceTemplate: newValue,
                      });
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            className={DEFAULT_DRAG_HANDLE_SELECTOR}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "15px 15px",
              cursor: "grab",
            }}
          >
            <Button
              sx={{
                borderRadius: "15px",
                height: "30px",
              }}
              startIcon={<CloseIcon />}
              onClick={() => setSelectedCompanyId("")}
              variant="outlined"
            >
              {t("common:cancelShort")}
            </Button>

            <Button
              disabled={disableContinue}
              sx={{
                borderRadius: "15px",
                height: "30px",
              }}
              startIcon={<CheckIcon />}
              onClick={() => handleComplete()}
              variant="contained"
            >
              {t("common:complete")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UnionPaymentInfoModal;
